/**
 * Who can open an artifact's link: only its owner, people they name by
 * email, people in their organisation (offered while they are in one), or
 * anyone with the link. A
 * link shows the version shown when it was shared, or always the latest, and
 * can stop working at a time chosen here. Private ends the link; sharing
 * again makes a new one.
 */

import { useState } from 'react';
import { MAX_SHARE_PEOPLE } from '@wire';
import { ApiError, shareArtifact, type Artifact, type ArtifactShare, type ShareLevel } from '../api';
import { ds } from '../ds';
import { MenuItem } from './ui';

const LEVEL_LABEL: Record<ShareLevel, string> = {
  private: 'Private',
  people: 'People you choose',
  organisation: 'People in your organisation',
  link: 'Anyone with the link'
};

const DAY = 24 * 60 * 60;
const EXPIRY: Array<{ value: string; label: string; seconds: number | null }> = [
  { value: 'never', label: 'Never', seconds: null },
  { value: 'day', label: 'In a day', seconds: DAY },
  { value: 'week', label: 'In a week', seconds: 7 * DAY },
  { value: 'month', label: 'In 30 days', seconds: 30 * DAY }
];

const when = (at: number) =>
  new Date(at * 1000).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function ShareDialog({
  artifact,
  shownVersion,
  onSaved,
  onClose
}: {
  artifact: Artifact;
  /** The version in the panel: what a new link shows. */
  shownVersion: number;
  onSaved: (share: ArtifactShare) => void;
  onClose: () => void;
}) {
  const { Dialog, Button, Checkbox, Select } = ds();
  const [share, setShare] = useState(artifact.share);
  const [level, setLevel] = useState<ShareLevel>(artifact.share.level);
  const [latest, setLatest] = useState(artifact.share.level !== 'private' && artifact.share.version === null);
  const [expiry, setExpiry] = useState(artifact.share.expiresAt ? 'kept' : 'never');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [peopleText, setPeopleText] = useState(artifact.share.people.join(', '));
  const people = [...new Set(peopleText.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];

  const link = share.path ? new URL(share.path, window.location.origin).href : null;
  const version = share.level !== 'private' && share.version !== null ? share.version : shownVersion;

  const save = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const seconds = EXPIRY.find((e) => e.value === expiry)?.seconds;
    const expiresAt = expiry === 'kept' ? share.expiresAt : seconds ? Math.floor(Date.now() / 1000) + seconds : null;
    shareArtifact(
      artifact.id,
      level === 'private' ? { level } : { level, version: latest ? null : version, expiresAt, ...(level === 'people' ? { people } : {}) }
    )
      .then((next) => {
        setShare(next);
        onSaved(next);
        setCopied(false);
      })
      .catch((err: unknown) => setError(err instanceof ApiError && err.status !== 0 ? err.message : 'That did not go through. Try again.'))
      .finally(() => setBusy(false));
  };

  const copy = () => {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => setCopied(true), () => setCopied(false));
  };

  const changed =
    level !== share.level ||
    (level === 'people' && people.join(',') !== share.people.join(',')) ||
    (level !== 'private' && (latest !== (share.version === null) || (expiry !== 'kept' && (expiry !== 'never' || share.expiresAt !== null))));

  return (
    <Dialog
      open
      position="fixed"
      width={480}
      title={`Share “${artifact.title}”`}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
            Done
          </Button>
          <Button variant="primary" density="app" size="sm" disabled={busy || !changed || (level === 'people' && !people.length)} onClick={save}>
            {level === 'private' && share.level !== 'private' ? 'Stop sharing' : 'Save'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div role="radiogroup" aria-label="Who can open it" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {artifact.shareLevels.map((l) => (
            <MenuItem key={l} label={LEVEL_LABEL[l]} selected={level === l} onSelect={() => setLevel(l)} />
          ))}
        </div>
        {level === 'people' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-body)' }}>
            Email addresses
            <textarea
              value={peopleText}
              rows={2}
              placeholder="ada@example.com, grace@example.com"
              onChange={(e) => setPeopleText(e.target.value)}
              style={{
                resize: 'vertical',
                padding: '8px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                background: 'var(--app-panel-sunken)',
                color: 'var(--text-body)',
                font: 'inherit',
                fontSize: 13
              }}
            />
            {people.length > MAX_SHARE_PEOPLE ? (
              <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>At most {MAX_SHARE_PEOPLE} people.</span>
            ) : null}
          </label>
        ) : null}
        {level !== 'private' ? (
          <>
            <Checkbox
              label="Always show the latest version"
              {...(latest ? {} : { description: `It shows version ${version}.` })}
              checked={latest}
              onChange={setLatest}
            />
            <Select
              label="Stops working"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              options={[
                ...(share.expiresAt ? [{ value: 'kept', label: when(share.expiresAt) }] : []),
                ...EXPIRY.map(({ value, label }) => ({ value, label }))
              ]}
            />
          </>
        ) : null}
        {link && share.level !== 'private' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              readOnly
              aria-label="Link"
              value={link}
              onFocus={(e) => e.target.select()}
              style={{
                flex: 1,
                minWidth: 0,
                height: 34,
                padding: '0 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-subtle)',
                background: 'var(--app-panel-sunken)',
                color: 'var(--text-body)',
                font: 'inherit',
                fontSize: 13
              }}
            />
            <Button variant="accent" density="app" size="sm" iconLeft={copied ? 'check' : 'copy'} onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        ) : null}
        {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
      </div>
    </Dialog>
  );
}
